import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowBack, Save } from "@mui/icons-material";
import { useCompany, useCreateCompany, useUpdateCompany } from "@/hooks/useCRM";
import { PIPELINE_LABELS, type SizeBand } from "@/types";
import ChipListEditor from "@/components/ChipListEditor";

const SIZE_LABELS: Record<SizeBand, string> = {
  sole_trader: "Sole Trader",
  small: "Small (1-50)",
  medium: "Medium (51-250)",
  large: "Large (251-1000)",
  enterprise: "Enterprise (1000+)",
};

export default function CRMCompanyEdit() {
  const { companyId } = useParams<{ companyId: string }>();
  const isNew = !companyId;
  const navigate = useNavigate();

  const { data: existing, isLoading } = useCompany(isNew ? undefined : companyId);
  const createMutation = useCreateCompany();
  const updateMutation = useUpdateCompany(companyId ?? "");

  const [form, setForm] = useState<Record<string, unknown>>({
    name: "",
    industry: "",
    size_band: "",
    website: "",
    address_line1: "",
    address_line2: "",
    city: "",
    postcode: "",
    country: "United Kingdom",
    phone: "",
    email_general: "",
    source: "",
    pipeline_stage: "lead",
    notes: "",
    tags: [],
    assigned_to: "",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  useEffect(() => {
    if (existing) {
      setForm({
        name: existing.name,
        industry: existing.industry ?? "",
        size_band: existing.size_band ?? "",
        website: existing.website ?? "",
        address_line1: existing.address_line1 ?? "",
        address_line2: existing.address_line2 ?? "",
        city: existing.city ?? "",
        postcode: existing.postcode ?? "",
        country: existing.country ?? "United Kingdom",
        phone: existing.phone ?? "",
        email_general: existing.email_general ?? "",
        source: existing.source ?? "",
        pipeline_stage: existing.pipeline_stage,
        notes: existing.notes ?? "",
        tags: existing.tags ?? [],
        assigned_to: existing.assigned_to ?? "",
      });
    }
  }, [existing]);

  const handleSave = async () => {
    const data = { ...form };
    // Clean empty strings to null
    for (const key of Object.keys(data)) {
      if (data[key] === "") data[key] = null;
    }
    if ((data.tags as string[])?.length === 0) data.tags = null;

    try {
      if (isNew) {
        const company = await createMutation.mutateAsync(data);
        navigate(`/crm/companies/${company.id}`);
      } else {
        await updateMutation.mutateAsync(data);
        setSnackbar({ open: true, message: "Company saved", severity: "success" });
      }
    } catch {
      setSnackbar({ open: true, message: "Failed to save", severity: "error" });
    }
  };

  const update = (field: string, value: unknown) => setForm((f) => ({ ...f, [field]: value }));

  if (!isNew && isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(isNew ? "/crm" : `/crm/companies/${companyId}`)}
        >
          Back
        </Button>
        <Typography variant="h4" sx={{ flex: 1 }}>
          {isNew ? "New Company" : `Edit: ${existing?.name ?? ""}`}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={handleSave}
          disabled={isPending || !form.name}
        >
          {isPending ? "Saving..." : "Save"}
        </Button>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
        <Card>
          <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="h6">Company Details</Typography>
            <TextField
              label="Company Name"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Industry"
              value={form.industry}
              onChange={(e) => update("industry", e.target.value)}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Size</InputLabel>
              <Select
                value={form.size_band}
                label="Size"
                onChange={(e) => update("size_band", e.target.value)}
              >
                <MenuItem value="">Not specified</MenuItem>
                {Object.entries(SIZE_LABELS).map(([k, v]) => (
                  <MenuItem key={k} value={k}>
                    {v}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Website"
              value={form.website}
              onChange={(e) => update("website", e.target.value)}
              fullWidth
            />
            <TextField
              label="Phone"
              value={form.phone}
              onChange={(e) => update("phone", e.target.value)}
              fullWidth
            />
            <TextField
              label="General Email"
              value={form.email_general}
              onChange={(e) => update("email_general", e.target.value)}
              fullWidth
            />
            <TextField
              label="Source"
              value={form.source}
              onChange={(e) => update("source", e.target.value)}
              fullWidth
              placeholder="How did you find them?"
            />
          </CardContent>
        </Card>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Card>
            <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography variant="h6">Pipeline & Assignment</Typography>
              <FormControl fullWidth>
                <InputLabel>Pipeline Stage</InputLabel>
                <Select
                  value={form.pipeline_stage}
                  label="Pipeline Stage"
                  onChange={(e) => update("pipeline_stage", e.target.value)}
                >
                  {Object.entries(PIPELINE_LABELS).map(([k, v]) => (
                    <MenuItem key={k} value={k}>
                      {v}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Assigned To"
                value={form.assigned_to}
                onChange={(e) => update("assigned_to", e.target.value)}
                fullWidth
              />
            </CardContent>
          </Card>

          <Card>
            <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography variant="h6">Address</Typography>
              <TextField
                label="Address Line 1"
                value={form.address_line1}
                onChange={(e) => update("address_line1", e.target.value)}
                fullWidth
              />
              <TextField
                label="Address Line 2"
                value={form.address_line2}
                onChange={(e) => update("address_line2", e.target.value)}
                fullWidth
              />
              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  label="City"
                  value={form.city}
                  onChange={(e) => update("city", e.target.value)}
                  sx={{ flex: 2 }}
                />
                <TextField
                  label="Postcode"
                  value={form.postcode}
                  onChange={(e) => update("postcode", e.target.value)}
                  sx={{ flex: 1 }}
                />
              </Box>
              <TextField
                label="Country"
                value={form.country}
                onChange={(e) => update("country", e.target.value)}
                fullWidth
              />
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <ChipListEditor
                label="Tags"
                values={(form.tags as string[]) ?? []}
                onChange={(v) => update("tags", v)}
              />
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <TextField
                label="Notes"
                value={form.notes}
                onChange={(e) => update("notes", e.target.value)}
                fullWidth
                multiline
                minRows={3}
              />
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
